import moment from "moment-timezone"
import { surfaceFields, surfacesError, surfacesOf } from "./CourtSurfaceChoices"
import { countryLanguage, hasText } from "components/Common/localizedText"
import { packageContent, playerPackageDraft } from "./eventPlayerPackage"
import { descriptionDraft } from "./eventTexts"
import { TENIS10, TENIS10_PLAYER_PACKAGE } from "./tenis10Event"

export const eventTimezones = {
  Romania: "Europe/Bucharest",
  Bulgaria: "Europe/Sofia",
  Moldova: "Europe/Chisinau",
  Hungary: "Europe/Budapest",
  Bahrain: "Asia/Bahrain",
  Armenia: "Asia/Yerevan",
  Czechia: "Europe/Prague",
}
export const noOrganizer = { firstName: "No", lastName: "organizer" }
// The Sport select's choices (PRD 16 LR11, the LR10 verdict E-C): the active sports, then Tenis10 while tennis is active — a tennis event
// of the Tenis10 programme, chosen at creation.
const sportName = key => key.charAt(0).toUpperCase() + key.slice(1)
export const sportChoices = settings => [
  ...(settings.activeSports || []).map(key => ({ key, name: sportName(key) })),
  ...(settings.activeSports?.includes("tennis") ? [{ key: TENIS10, name: "Tenis10" }] : []),
]
// The sport an event is created with, and its programme.
const eventSport = sportType => sportType === TENIS10 ? { sportType: "tennis", programType: TENIS10 } : { sportType }
// A Tenis10 event (PRD 16 LR15, the canvas verdict E-A, round 6): no official ball, its balls being the club's of each competition's colour;
// one competition per child, so one diploma per child; its creation's Logistics card chooses for the medals, the diplomas and the water
// only, the balls and the lanyards of each colour coming with the competitions (Events ADR 0016).
export const TENIS10_CARD_FAMILIES = ["medals", "diplomas", "water"]
const cardChoices = (choices = {}, tenis10) => tenis10
  ? Object.fromEntries(Object.entries(choices).filter(([family]) => TENIS10_CARD_FAMILIES.includes(family))) : choices
// The value an official ball select gives Balls TBD, which the event stores as no official ball.
export const BALLS_TBD = "balls-tbd"
// The official ball select's choices: the settings' balls, a ball the event keeps that the settings no
// longer list, and Balls TBD.
export const officialBallOptions = (balls = [], current) => [
  ...balls.map((ball) => ({ key: ball.name, name: ball.name })),
  ...(current?.name && !balls.some((ball) => ball.name === current.name) ? [{ key: current.name, name: current.name }] : []),
  { key: BALLS_TBD, name: "Balls TBD" },
]
// The official ball a select value chooses: null for Balls TBD, the settings' ball, or none.
export const officialBallFrom = (value, balls = []) =>
  value === BALLS_TBD ? null : balls.find((ball) => ball.name === value)
// At creation the ball starts unchosen (""); Balls TBD is null.
export const officialBallChosen = (value) => value !== "" && value !== undefined
export const provisionalVenue = (club) =>
  club && (club.clubName === null || club.clubName === "TBA")
export const eventCountry = (values) =>
  provisionalVenue(values.club)
    ? values.country
    : values.club?.location?.country
export const eventTimezone = (values) => eventTimezones[eventCountry(values)]
// The time zone under a day / hour picker of an event (P24, Tudor 2026-10-24): „Europe/Bucharest · Romania time”.
export const eventZoneLabel = (zone) => {
  const country = Object.keys(eventTimezones).find((name) => eventTimezones[name] === zone)
  return zone ? zone + (country ? " · " + country + " time" : "") : ""
}
// The end a new event's start suggests (P24, the canvas verdict A + C): the next day at 18:00, a weekend's Sunday afternoon.
export const endFromStart = (start) => {
  const day = moment.utc(String(start || "").slice(0, 10), "YYYY-MM-DD", true)
  return day.isValid() ? day.add(1, "day").format("YYYY-MM-DD") + "T18:00" : ""
}
// The settings' default player package: its texts by language, and the T-shirt. A Tenis10 event's is the Tenis10 package, in Romanian.
const defaultPackage = (settings, sportType) => sportType === TENIS10 ? { ro: TENIS10_PLAYER_PACKAGE } : settings?.playerPackages?.[0] || {}
// A new event's player package starts in its country's language, or in English when the default package has no text in that one; a
// Tenis10 event's in Romanian, whatever its club.
const defaultPackageLanguage = (country, pack, sportType) => {
  if (sportType === TENIS10) return "ro"
  const language = countryLanguage(country)
  return hasText(pack[language]) || !hasText(pack.en) ? language : "en"
}
// The player package's original as the creation form holds it: in the language the admin chose or wrote in, otherwise the default
// one; with the text the admin wrote, otherwise the default package's text in that language.
export const creationPackage = (values, settings) => {
  const pack = defaultPackage(settings, values.sportType)
  const language = values.playerPackageLanguage || defaultPackageLanguage(eventCountry(values), pack, values.sportType)
  return { language, content: values.playerPackage || packageContent(pack[language]) }
}
// The form values once the admin writes the original: the text, in the language it is shown in, which a later club no longer changes.
export const packageWritten = (values, settings, content) => ({ playerPackageLanguage: creationPackage(values, settings).language, playerPackage: content })
// The description's original as the creation form holds it: in the language the admin chose or wrote in, otherwise the country's.
export const creationDescription = (values) => ({
  language: values.descriptionLanguage || countryLanguage(eventCountry(values)),
  content: { body: values.description },
})
// The form values once the admin writes the description, in the language it is shown in, which a later club no longer changes. An editor
// that only moves its cursor writes nothing.
export const descriptionWritten = (values, content) => content.body === values.description ? {}
  : { descriptionLanguage: creationDescription(values).language, description: content.body }
const datetimeFormat = "YYYY-MM-DDTHH:mm"
export const eventInstant = (value, zone) => {
  if (!value || !zone) return null
  const parsed = moment.tz(value, datetimeFormat, true, zone)
  return parsed.isValid() && parsed.format(datetimeFormat) === value
    ? parsed.toISOString()
    : null
}
export const initialEventValues = (settings) => {
  return {
    name: "",
    sportType: settings.activeSports?.includes("tennis")
      ? "tennis"
      : settings.activeSports?.[0] || "",
    category: "",
    playMode: "weekend",
    season: "",
    featured: false,
    club: null,
    country: "",
    city: "",
    startDate: "",
    endDate: "",
    coordinator: null,
    organizer: null,
    directorName: "",
    email: "",
    phone: "",
    refereeName: "",
    supervisorName: "",
    singlesMaxNumber: 1,
    doublesMaxNumber: 1,
    numberOfCourts: "",
    courtType: "",
    // P92: every surface of the event, the first the main one (`courtType`)
    courtSurfaces: [],
    // Not chosen yet; Balls TBD is null, a ball is the settings' ball.
    officialBall: "",
    // The suppliers and the products the Logistics card changed, by generic family.
    logisticsSuppliers: {},
    logisticsProducts: {},
    // The description's original language as the admin chose it, and its rich text as they wrote it.
    descriptionLanguage: "",
    description: "",
    tShirt: false,
    // The player package's original language as the admin chose it, and its text as they wrote it; until then, the defaults.
    playerPackageLanguage: "",
    playerPackage: null,
  }
}
export const validateEventCreation = (values, settings) => {
  const errors = {}
  if (!values.name.trim()) errors.name = "Enter an event name."
  else if (values.name.trim().length > 200)
    errors.name = "Use at most 200 characters."
  if (!sportChoices(settings).some(({ key }) => key === values.sportType))
    errors.sportType = "Select a sport."
  for (const [field, options, label] of [
    ["category", "categories", "category"],
    ["playMode", "playModes", "format"],
    ["season", "season", "season"],
  ]) {
    if (!settings[options]?.some((option) => option.key === values[field]))
      errors[field] = "Select a " + label + "."
  }
  const surfaces = surfacesError(values, settings.courtSurfaces)
  if (surfaces) errors.courtSurfaces = surfaces
  if (values.sportType !== TENIS10 && !officialBallChosen(values.officialBall))
    errors.officialBall = "Choose an official ball or Balls TBD."
  if (!values.club || (!provisionalVenue(values.club) && !values.club.id))
    errors.club = "Select a club, TBA or No Club."
  if (values.club && !eventTimezone(values))
    errors.country =
      "Select a supported country to establish the event timezone."
  const zone = eventTimezone(values) || "UTC"
  const start = eventInstant(values.startDate, zone),
    end = eventInstant(values.endDate, zone)
  if (!start) errors.startDate = "Enter a valid start date and time."
  if (!end) errors.endDate = "Enter a valid end date and time."
  else if (start && end <= start)
    errors.endDate = "The end must be after the start."
  if (!settings.coordinators?.some((c) => c.name === values.coordinator?.name))
    errors.coordinator = "Select a coordinator."
  if (
    !values.organizer?.id &&
    !(
      values.organizer?.firstName === "No" &&
      values.organizer?.lastName === "organizer"
    )
  )
    errors.organizer = "Select an organizer or No organizer."
  if (values.email && !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(values.email.trim()))
    errors.email = "Enter a valid email address."
  for (const field of ["singlesMaxNumber", "doublesMaxNumber"]) {
    if (![1, 2, 3].includes(Number(values[field])))
      errors[field] = "Select a limit between 1 and 3."
  }
  if (
    values.numberOfCourts !== "" &&
    (!Number.isInteger(Number(values.numberOfCourts)) ||
      Number(values.numberOfCourts) < 0)
  ) {
    errors.numberOfCourts = "Enter a whole number of courts, zero or greater."
  }
  return errors
}
export const buildEventCreationPayload = (values, settings) => {
  const club = provisionalVenue(values.club)
    ? {
        clubName: values.club.clubName,
        location: {
          country: values.country,
          ...(values.city.trim()
            ? {
                city: values.city.trim(),
                name: values.city.trim() + ", " + values.country,
              }
            : {}),
        },
      }
    : values.club
  const organizer = values.organizer
  const playerPackage = creationPackage(values, settings)
  const description = creationDescription(values)
  return {
    name: values.name.trim(),
    ...eventSport(values.sportType),
    category: values.category,
    season: values.season,
    playMode: values.playMode,
    coordinator: values.coordinator,
    club,
    organizer: {
      id: organizer?.id,
      firstName: organizer?.firstName,
      lastName: organizer?.lastName,
      clientType: organizer?.clientType,
      companyName: organizer?.companyName,
      email: values.email.trim(),
      phone: values.phone.trim(),
    },
    startDate: eventInstant(values.startDate, eventTimezone(values)),
    endDate: eventInstant(values.endDate, eventTimezone(values)),
    directorName: values.directorName.trim(),
    refereeName: values.refereeName.trim(),
    supervisorName: values.supervisorName.trim(),
    singlesMaxNumber: values.sportType === TENIS10 ? 1 : Number(values.singlesMaxNumber),
    // A Tenis10 event has no doubles; the event still states the limit, so it keeps the lowest.
    doublesMaxNumber: values.sportType === TENIS10 ? 1 : Number(values.doublesMaxNumber),
    numberOfCourts:
      values.numberOfCourts === "" ? null : Number(values.numberOfCourts),
    ...surfaceFields(surfacesOf(values)),
    // Balls TBD is sent as no official ball, and so is a Tenis10 event's.
    officialBall: values.sportType === TENIS10 ? null : values.officialBall,
    logistics: { suppliers: cardChoices(values.logisticsSuppliers, values.sportType === TENIS10),
      products: cardChoices(values.logisticsProducts, values.sportType === TENIS10) },
    featured: values.featured,
    description: descriptionDraft(description.language, description.content.body),
    image: { logo: "", cover: "", featured: "" },
    playerPackage: playerPackageDraft(playerPackage.language, playerPackage.content, values.tShirt),
  }
}
